import { createContext, useContext, useEffect, useState, type FormEvent } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { api, getApiKey, setApiKey } from './api/client'
import type { AppSchema, Health } from './types'
import { Layout } from './components/Layout'
import { LoadingBlock } from './components/ui'
import { DashboardPage } from './pages/DashboardPage'
import { RegistryPage } from './pages/RegistryPage'
import { ExperimentsPage } from './pages/ExperimentsPage'
import { AnalyticsPage } from './pages/AnalyticsPage'
import { ValidationPage } from './pages/ValidationPage'
import { ExtractionPage } from './pages/ExtractionPage'
import { ExportPage } from './pages/ExportPage'
import { SettingsPage } from './pages/SettingsPage'
import { AuditPage } from './pages/AuditPage'

const SchemaContext = createContext<AppSchema>({})
export const useAppSchema = () => useContext(SchemaContext)

export default function App() {
  const [health, setHealth] = useState<Health | null>(null)
  const [schema, setSchema] = useState<AppSchema>({})
  const [unlocked, setUnlocked] = useState(false)
  const [bootError, setBootError] = useState('')

  useEffect(() => {
    void (async () => {
      try {
        const h = await api.health(); setHealth(h)
        if (!h.auth_required) setUnlocked(true)
        else if (getApiKey()) {
          try { await api.dashboard(); setUnlocked(true) } catch { setApiKey('') }
        }
      } catch (e) { setBootError(e instanceof Error ? e.message : String(e)) }
    })()
  }, [])

  useEffect(() => {
    if (!unlocked) return
    void api.schema().then(setSchema).catch(e => setBootError(e instanceof Error ? e.message : String(e)))
  }, [unlocked])

  if (bootError) return <div className="boot-screen"><div className="boot-card"><h1>DCS / THERMOLYSIS</h1><p>Backend недоступен: {bootError}</p><button onClick={() => location.reload()}>Повторить</button></div></div>
  if (!health) return <div className="boot-screen"><LoadingBlock label="Подключаем научный реестр…"/></div>
  if (!unlocked) return <Unlock onDone={() => setUnlocked(true)} />
  if (!Object.keys(schema).length) return <div className="boot-screen"><LoadingBlock label="Получаем схему данных…"/></div>

  return <SchemaContext.Provider value={schema}>
    <Routes>
      <Route element={<Layout health={health} onLock={() => { setApiKey(''); setUnlocked(!health.auth_required) }} />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/sources" element={<RegistryPage table="sources" title="Источники" subtitle="Каталог литературы, DOI и статус обработки" columns={['source_id','title','year','journal','priority','extraction_status']} />} />
        <Route path="/extraction" element={<ExtractionPage />} />
        <Route path="/experiments" element={<ExperimentsPage />} />
        <Route path="/compounds" element={<RegistryPage table="compounds" title="Соединения" subtitle="Прекурсоры и нормализованные химические признаки" columns={['compound_id','formula_normalized','cation_metal','anion_metal','cation_ligand','anion_ligand','family']} />} />
        <Route path="/results" element={<RegistryPage table="phase_observations" title="Результаты" subtitle="Наблюдения точных фаз с сохранением provenance" columns={['experiment_id','phase_id','presence','fraction_pct','identification_method','evidence_strength']} />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/validation" element={<ValidationPage />} />
        <Route path="/kinetics" element={<RegistryPage table="kinetics" title="Кинетика" subtitle="Стадии, диапазоны температур и энергия активации" columns={['compound_id','experiment_id','stage_index','temperature_from_C','temperature_to_C','method','Ea_kJ_mol']} />} />
        <Route path="/phases" element={<RegistryPage table="phases" title="Фазы" subtitle="Канонический справочник фаз" columns={['phase_id','canonical_name','formula','phase_group','aliases']} />} />
        <Route path="/measurements" element={<RegistryPage table="measurements" title="Измерения" subtitle="BET, residual carbon и другие числовые outputs" columns={['experiment_id','metric','value','unit','method','confidence']} />} />
        <Route path="/features" element={<RegistryPage table="feature_registry" title="Признаковое пространство" subtitle="Управление аналитическими признаками без миграции UI" columns={['label_ru','group_name','role','data_type','unit','enabled','missing_policy']} />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/export" element={<ExportPage />} />
        <Route path="/settings" element={<SettingsPage health={health} />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </SchemaContext.Provider>
}

function Unlock({ onDone }: { onDone: () => void }) {
  const [key, setKey] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError(''); setApiKey(key.trim())
    try { await api.dashboard(); onDone() }
    catch (err) { setApiKey(''); setError(err instanceof Error ? err.message : String(err)) }
    finally { setBusy(false) }
  }
  return <div className="boot-screen auth-screen"><form className="auth-card" onSubmit={submit}>
    <div className="auth-mark">DCS</div><h1>Thermolysis Registry</h1><p>Введите API-ключ реестра. Ключ хранится только в текущей вкладке браузера.</p>
    <label><span>API key</span><input type="password" value={key} onChange={e=>setKey(e.target.value)} autoFocus placeholder="••••••••••••••••" /></label>
    {error && <div className="auth-error">{error}</div>}
    <button className="btn btn-primary" disabled={!key.trim() || busy}>{busy ? 'Проверяем…' : 'Открыть рабочее пространство'}</button>
  </form></div>
}
